import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {AppService} from './services/app.service';
import {VcrClassroomComponent} from './components/vcr-classroom/vcr-classroom.component';
import {NgxsReduxDevtoolsPluginModule} from '@ngxs/devtools-plugin';
import {NgxsLoggerPluginModule} from '@ngxs/logger-plugin';
import {NgxsModule, Store} from '@ngxs/store';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {WRTCProviderService} from './modules/wrtc/services/providers/wrtc-provider';
import {wrtcProviderFactory} from './modules/wrtc/services/WRTCProviderFactory';
import {LocalUserState} from '@app/state/local-user.state';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ClassroomState} from '@app/state/classroom-state.service';
import {GameModeState} from '@app/state/game-mode.state';
import {NgxsResetPluginModule} from 'ngxs-reset-plugin';
import {WebcamModule} from 'ngx-webcam';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {AssignmentState} from '@app/state/assignment.state';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {WhiteboardModule} from '@app/modules/whiteboard/whiteboard.module';
import {WhiteboardState} from '@app/state/whiteboard.state';
import {BlackboardState} from '@app/state/blackboard.state';
import {SharedModule} from '@app/modules/shared/shared.module';
import {AuthModule} from '@app/modules/auth/auth.module';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {DiagnosticsModule} from '@app/modules/diagnostics/diagnostics.module';
import {BlackboardModule} from '@app/modules/blackboard/blackboard.module';
import {ChatModule} from '@app/modules/chat/chat.module';
import {WrtcModule} from '@app/modules/wrtc/wrtc.module';
import {InterceptorService} from '@app/services/interceptor.service';
import {RemoteUsersInfoState} from '@app/state/remote-users.state';
import { MainPageComponent } from './components/main-page/main-page.component';
import { CurriculumComponent } from './components/curriculum/curriculum.component';
import {ServiceInfoState} from '@app/state/service-info.state';
import {JsonLogService} from '@app/modules/shared/services/json-log.service';
import {UsersListService} from '@app/modules/shared/services/users-list.service';
import {GlobalErrorHandler} from '@app/error-handler/global-error-handler';
import {IncorrectUrlComponent} from '@app/modules/shared/components/incorrect-url/incorrect-url.component';
import {UserDisconnectionHandlerService} from '@app/modules/shared/services/user-disconnection-handler.service';
import {PrivateWhiteboardState} from '@app/state/private-whiteboard.state';
import {DragDropModule} from "@angular/cdk/drag-drop";
import {ReportState} from "@app/state/report.state";

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    audioContext: typeof AudioContext;
    webkitAudioContext: typeof AudioContext;
    smartsupp: typeof Function | any;
  }

  interface HTMLMediaElement {
    setSinkId?(sinkId: string): Promise<void>;
    sinkId: string; // Also include the sinkId property
  }
}

export function onAppBeforeInitialization(appService: AppService) {
  return () => appService.onAppBeforeInitialization().then();
}

const interceptorProviders =
  [
    {provide: HTTP_INTERCEPTORS, multi: true, useClass: InterceptorService},
  ];

@NgModule({
  declarations: [
    AppComponent,
    VcrClassroomComponent,
    MainPageComponent,
    CurriculumComponent,
    IncorrectUrlComponent,
  ],
  imports: [
    BrowserModule,
    ReactiveFormsModule,
    FormsModule,
    AppRoutingModule,
    HttpClientModule,
    WebcamModule,
    BsDropdownModule,
    BrowserAnimationsModule,
    PopoverModule,
    NgbModule,
    WhiteboardModule,
    SharedModule,
    AuthModule,
    DiagnosticsModule,
    BlackboardModule,
    ChatModule,
    WrtcModule,
    DragDropModule,

    NgxsModule.forRoot([
      ServiceInfoState,
      WhiteboardState,
      PrivateWhiteboardState,
      BlackboardState,
      RemoteUsersInfoState,
      LocalUserState,
      AssignmentState,
      ReportState,
      ClassroomState,
      GameModeState
    ]),
    NgxsResetPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
  ],
  providers: [
    interceptorProviders,
    AppService,
    WebSocketService,
    {
      provide: APP_INITIALIZER,
      useFactory: onAppBeforeInitialization,
      deps: [AppService],
      multi: true
    },
    {
      provide: WRTCProviderService,
      useFactory: wrtcProviderFactory,
      deps: [Store, JsonLogService, UsersListService, UserDisconnectionHandlerService, HttpClient],
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ],
  exports: [],

  bootstrap: [AppComponent]
})
export class AppModule {
}
